// Shaders written for this app — no Shadertoy API, no quota, no attribution, no NC clause.
//
// These exist because every Shadertoy route has a tax: the API key is capped at 1500
// requests/month and currently rate limited, the endpoint sits behind a bot challenge that
// blocks headless renders, and the default licence is CC BY-NC-SA — where the NC clause is
// a live problem for monetized clips. Code authored here has none of that.
//
// They also use something no Shadertoy shader can: `iBeat`, the quarter-note position in
// the song (fragmentHeader, shaders.civet). Shadertoy has no concept of musical time, so
// its audio shaders infer rhythm from FFT peaks and lag or misfire. These lock to the
// actual grid — `fract(iBeat)` is the phase inside the current beat and `floor(iBeat)`
// counts them, so a downbeat lands exactly on the downbeat.
//
// Constraints these are written against:
//   - single pass, no feedback buffer, so trails are procedural rather than accumulated
//   - iChannel0 is binCount x 1, RGBA = FreqL, FreqR, TimeL, TimeR (renderer.civet:1745)
//   - GLSL ES 3.00: `texture()`, never `texture2D()`
//   - only the uniforms in fragmentHeader — no iChannelResolution, iDate, iFrameRate
//   - a vertical 540x960 frame upscaled 2x, with the staff scrolling over the middle
//   - a clip is played on repeat, so the frame after the last one is the first one. Nothing
//     may advance linearly with iTime: route scrolls through loopPhase, sin/cos rates
//     through loopRateAngular, anything hashed on a cell index through loopCell, and a walk
//     through a noise field through loopDrift (all in shaders.civet). A rate multiplied by
//     audio cannot loop at all — iTime * bass accumulates — so make the bass a bounded
//     displacement instead. Terms driven by iBeat are already seamless on a whole bar.
//
// Verify after editing:  node scripts/shadertoy/check-shaders.mjs
// which measures the loop seam at four clip lengths as well as compiling and scaling.

// Rings on the musical grid rather than on detected peaks.
beatBloom := """
#define RINGS 4

// t 0..1 across the spectrum. Bins are linear in frequency over ~0-22kHz, so nearly all
// musical energy sits in the first few — cubing t spends the resolution where the notes are.
float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) {
    s += fft(float(i) / 64.0);
  }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = (fragCoord * 2.0 - iResolution.xy) / iResolution.y;

  float b = bass();
  vec3 col = vec3(0.0);

  // One ring per recent beat. Ring i was emitted i beats ago, so it has been expanding
  // that much longer — the whole history is a function of iBeat, which is what lets a
  // single pass show four concurrent rings with no feedback buffer.
  for (int i = 0; i < RINGS; i++) {
    float age = fract(iBeat) + float(i);
    float beatIndex = floor(iBeat) - float(i);
    float bar = mod(beatIndex, 4.0) < 0.5 ? 1.0 : 0.0;

    float radius = age * 0.42;
    float fade = exp(-age * 0.9);
    float thickness = mix(0.012, 0.030, bar) * (1.0 + b * 1.5);
    float ring = thickness / (abs(length(uv) - radius) + thickness);

    vec3 tint = mix(vec3(0.25, 0.55, 1.00), vec3(1.00, 0.55, 0.20), bar);
    col += tint * ring * fade * 0.55;
  }

  // Spectrum wrapped around the circle, so the halo has the song's shape in it.
  // atan(0, 0) is undefined in GLSL, and uv lands exactly on the origin whenever both
  // dimensions are odd — the epsilon costs nothing and removes the singularity.
  //
  // Mirrored (|angle| / pi) rather than wrapped (angle / 2pi + 0.5): a wrap puts the loud
  // low bins and the near-silent high bins next to each other across the -x axis, and the
  // discontinuity shows up as a seam and a hard-edged wedge. Mirroring runs the spectrum
  // out and back, so both halves meet at matching values and the halo is continuous.
  float ang = abs(atan(uv.y, uv.x + 1e-8)) * 0.31830989;
  float e = fft(ang);
  col += vec3(0.35, 0.20, 0.60) * e * e * 0.6;

  col *= 1.0 - 0.35 * smoothstep(0.0, 1.2, length(uv));
  fragColor = vec4(col, 1.0);
}
"""

// Quantised columns, each lit by its own band. Cell edges suit the app's pixel art.
spectrumRain := """
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

float band(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 2.5), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;

  const float cols = 24.0;
  // Derive rows from the aspect so cells stay square in a 9:16 frame.
  float rows = max(floor(cols * iResolution.y / iResolution.x), 1.0);
  float cx = floor(uv.x * cols);
  float cy = floor(uv.y * rows);

  float e = band((cx + 0.5) / cols);

  // A single pass has no history to smear, so the trail is computed from time instead of
  // accumulated: each column has its own speed and phase, and brightness falls off behind
  // a head position that is a pure function of iTime.
  // Each column's rate is snapped on its own, so every column completes a whole number of
  // its own descents per loop and they all arrive back at their starting heights together.
  float speed = 2.0 + hash(vec2(cx, 7.0)) * 3.0;
  float head = fract(hash(vec2(cx, 1.0)) + iTime * loopRate(speed * 0.15)) * rows;
  float behind = mod(head - cy, rows);
  float trail = exp(-behind * 0.45);

  float lit = trail * (0.25 + e * 2.2);
  // Per-cell sparkle, rehashed 8 times a second. The tick is a hashed index, so it needs
  // both halves: a snapped rate to land on a whole tick at the loop point, and a wrapped
  // index so that tick is the one the first frame used.
  float flickerRate = loopRate(8.0);
  lit *= 0.75 + 0.5 * hash(vec2(cx, cy + loopCell(floor(iTime * flickerRate), flickerRate * iLoopTime)));

  vec3 col = mix(vec3(0.10, 0.35, 0.90), vec3(0.60, 0.95, 1.00), clamp(e * 2.0, 0.0, 1.0)) * lit;
  col += vec3(0.90, 0.98, 1.00) * smoothstep(0.85, 1.0, trail) * 0.7;

  fragColor = vec4(col, 1.0);
}
"""

// The stereo scope run along the long axis of a vertical frame.
waveformRibbon := """
void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;

  // Sampled along y so the ribbon runs top to bottom rather than across a narrow frame.
  vec4 a = texture(iChannel0, vec2(uv.y, 0.0));
  // getByteTimeDomainData is centred at 0.5 for silence, so subtract to get a signed swing.
  float l = a.z - 0.5;
  float r = a.w - 0.5;

  const float amp = 0.42;
  float dl = abs(uv.x - (0.5 + l * amp));
  float dr = abs(uv.x - (0.5 + r * amp));

  const float glow = 0.006;
  vec3 col = vec3(0.0);
  col += vec3(0.20, 0.70, 1.00) * glow / (dl + glow);
  col += vec3(1.00, 0.35, 0.70) * glow / (dr + glow);

  // Fill between the channels: the wider the stereo image, the more it blooms.
  float width = abs(l - r);
  float centre = smoothstep(0.0, 1.0, 1.0 - abs(uv.x - 0.5) * 2.0);
  col += vec3(0.35, 0.20, 0.55) * smoothstep(0.0, 0.6, width) * centre * 0.5;

  // Beat tick down both edges, clear of the staff in the middle of the frame.
  float pulse = exp(-fract(iBeat) * 5.0);
  float edge = smoothstep(0.06, 0.0, min(uv.x, 1.0 - uv.x));
  col += vec3(1.00, 0.90, 0.60) * pulse * edge * 0.8;

  fragColor = vec4(col, 1.0);
}
"""

// ── retro / synthwave ────────────────────────────────────────────────────────
// These quantise to a virtual pixel grid before doing anything else, which is what makes
// them chunky. The cell size is derived from iResolution rather than fixed in pixels —
// a fixed pixel cell would give a different chunk count at every output size, which is
// exactly the resolution dependence check-shaders.mjs exists to catch. Deriving it keeps
// the same virtual grid from 540x960 up to QHD, so the look is identical and only the
// crispness of each block changes.

// Perspective floor, banded sun, horizon. The genre's whole vocabulary in one frame.
neonHorizon := """
#define VROWS 160.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) {
    s += fft(float(i) / 64.0);
  }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;

  float aspect = iResolution.x / iResolution.y;
  float b = bass();
  float beat = exp(-fract(iBeat) * 4.0);
  // The horizon sits high so the grid gets the majority of a vertical frame.
  const float horizon = 0.62;

  vec3 col;
  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);

    vec3 sky = mix(vec3(0.30, 0.04, 0.32), vec3(0.04, 0.01, 0.12), smoothstep(0.0, 0.34, p.y));
    col = sky;

    float radius = 0.20 + 0.02 * b;
    float d = length(p - vec2(0.0, 0.13));
    float disc = step(d, radius);
    // Classic banded sun: horizontal cuts that thicken toward the bottom of the disc.
    float across = (p.y - 0.13 + radius) / (2.0 * radius);
    float bands = step(0.45, fract(across * 9.0));
    float cut = mix(1.0, bands, smoothstep(0.80, 0.10, across));
    vec3 sunCol = mix(vec3(1.00, 0.20, 0.55), vec3(1.00, 0.85, 0.25), clamp(across, 0.0, 1.0));

    col = mix(col, sunCol, disc * cut);
    col += vec3(1.00, 0.25, 0.55) * exp(-d * 6.0) * 0.22;
  } else {
    // Floor plane: 1/depth is the perspective, and scrolling z pulls it toward the viewer.
    // The world scale multipliers set how many lines are in shot — too small and the near
    // field runs out of grid and goes black, which is the whole bottom of a tall frame.
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = (uv.x - 0.5) * z * aspect * 8.0;
    // Snapped so a whole number of grid cells passes in one loop — see loopRate.
    float zw = z * 2.0 + loopPhase(2.0, iTime);

    float gx = abs(fract(xw) - 0.5);
    float gz = abs(fract(zw) - 0.5);
    float weight = 0.06 + 0.05 * beat;
    float line = max(smoothstep(weight, 0.0, gx), smoothstep(weight, 0.0, gz));

    // Grid lines converge to nothing at the vanishing point. Fading them out there is what
    // keeps the far field from turning into aliased fizz.
    float settle = smoothstep(0.0, 0.09, depth);

    vec3 grid = mix(vec3(0.15, 0.90, 1.00), vec3(0.70, 0.10, 0.95), clamp(depth * 2.2, 0.0, 1.0));
    col = vec3(0.03, 0.00, 0.07) + grid * line * settle * (0.75 + b * 1.6);
  }

  // Scanlines on the virtual grid, so they stay chunky at every output size.
  col *= 0.82 + 0.18 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// The spectrum as a neon skyline: each band is a building, lit windows and all.
spectrumSkyline := """
#define VROWS 140.0
#define BARS 14.0
// The band the bars span, as a fraction of Nyquist (24 kHz at the render's 48 kHz rate):
// roughly 55 Hz to 6 kHz. Below that is rumble and above it is air, and a bar aimed at
// either never moves.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span.
//
// This used to point-sample a single texel per bar — 14 of the 512 bins, 97% of the
// spectrum unread — so each bar tracked whichever bin it happened to land on. A melody
// walking up a scale slid between sampled bins and the bars twitched rather than followed
// it. Averaging the span means a bar answers to all the energy inside it.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;

  float aspect = iResolution.x / iResolution.y;
  float beat = exp(-fract(iBeat) * 5.0);

  vec3 col = mix(vec3(0.05, 0.01, 0.11), vec3(0.26, 0.03, 0.30), uv.y);

  // Sun low behind the skyline, so the buildings read as silhouettes against it.
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.34);
  float d = length(p);
  float across = (p.y + 0.26) / 0.52;
  float disc = step(d, 0.26);
  float bands = step(0.45, fract(across * 8.0));
  float cut = mix(1.0, bands, smoothstep(0.85, 0.15, across));
  vec3 sunCol = mix(vec3(1.00, 0.25, 0.50), vec3(1.00, 0.80, 0.30), clamp(across, 0.0, 1.0));
  col = mix(col, sunCol, disc * cut);
  col += vec3(1.00, 0.30, 0.50) * exp(-d * 7.0) * 0.18;

  float i = floor(uv.x * BARS);
  // Half-octave bars — equal MUSICAL width. The old pow(t, 2.5) warp crammed the leftmost
  // four bars into the bottom 3% of the spectrum, where they all read the same low rumble
  // and moved as one blob.
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi);
  // Music loses level as frequency rises, so without a tilt the skyline is a fixed
  // downward ramp with the right-hand bars parked on the floor whatever is playing.
  e *= 1.0 + 1.6 * (i / (BARS - 1.0));
  e = clamp(e, 0.0, 1.0);
  // Compressed rather than linear: a raw spectrum leaves everything above the low bins
  // flat on the floor, so the skyline becomes one tall block on the left and nothing else.
  float height = 0.10 + pow(e, 0.65) * 0.50;

  if (uv.y < height) {
    // The buildings need visible mass, or the lit windows float in the dark and the whole
    // thing reads as confetti instead of a skyline.
    vec3 face = mix(vec3(0.10, 0.04, 0.20), vec3(0.17, 0.06, 0.30), uv.y / max(height, 1e-4));
    // Darken toward each bar's edges so neighbouring buildings separate instead of
    // merging into one mass.
    float edge = smoothstep(0.0, 0.10, abs(fract(uv.x * BARS) - 0.5));
    face *= 0.55 + 0.45 * edge;

    // Windows two virtual cells across, so each building carries a readable grid of them
    // rather than a field of single-cell dither.
    vec2 g = floor(px / (cell * 2.0));
    float lit = step(0.62, hash(g + i * 13.0));
    vec3 windowCol = mix(vec3(1.00, 0.60, 0.15), vec3(0.35, 0.95, 1.00), step(0.5, hash(g + 3.0)));
    col = face + windowCol * lit * (0.35 + e * 1.1);
  }

  // Neon rim along each roofline, brightest on the beat. Thick enough to survive the
  // virtual grid — a sub-cell rim just disappears into the quantisation.
  float roof = smoothstep(cell / iResolution.y * 4.0, 0.0, abs(uv.y - height));
  col += mix(vec3(0.20, 0.95, 1.00), vec3(1.00, 0.20, 0.75), step(0.5, fract(i * 0.37))) * roof * (0.9 + beat * 1.1);

  col *= 0.84 + 0.16 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Square tunnel racing at the viewer, speed pushed by the low end.
chromeTunnel := """
#define VROWS 150.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) {
    s += fft(float(i) / 64.0);
  }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = (px * 2.0 - iResolution.xy) / iResolution.y;

  float b = bass();

  // Chebyshev distance makes the tunnel square rather than round, which reads as retro
  // 3D far more than a circular one does. Clamped so the vanishing point cannot divide
  // by zero at the exact centre of an odd-sized frame.
  float r = max(max(abs(uv.x), abs(uv.y)), 1e-4);
  float z = 1.0 / r;
  float ang = atan(uv.y, uv.x + 1e-8) * 0.15915494 + 0.5;

  // Slower than it wants to be. Slab pattern rate is the strobe rate, and this is a
  // full-field pattern — every slab that passes changes the brightness of the whole frame.
  // Bass as a displacement, not a speed-up — iTime * b accumulates and so cannot come back
  // at the loop point however the base rate is chosen. Kept under a slab so a hit pumps the
  // pattern without shunting it across a boundary, which would flip the whole frame's
  // colour on the beat.
  float rate = loopRate(0.9);
  float t = iTime * rate + b * 0.30;
  float phase = z * 0.6 - t;
  // Wrapped, because the colour below is a hash of the slab INDEX: one loop advances a
  // whole number of slabs, which puts the pattern back in place but hands each slab its
  // neighbour's colour unless the index wraps with it.
  float slab = loopCell(floor(phase), rate * iLoopTime);
  // Cosine rather than step. A hard step snaps the entire frame between two brightness
  // levels each time the pattern advances, which is where the strobe came from; a
  // continuous envelope spreads that same contrast over many frames.
  float bands = 0.5 - 0.5 * cos(6.2831853 * fract(phase));
  float spokes = step(0.5, fract(ang * 12.0));

  // Alternate hard between the two neon ends rather than blending through the middle:
  // interpolated values land on muddy desaturated purple, which is the opposite of neon.
  vec3 neon = mix(vec3(1.00, 0.12, 0.62), vec3(0.15, 0.92, 1.00), step(0.5, fract(slab * 0.37)));
  // Normalise the two to equal luminance. Magenta and cyan at full chroma differ by more
  // than 2x in brightness, so alternating between them is itself a full-frame flash even
  // once the band envelope is smooth — the hue changes, the eye reads a flash.
  neon *= 0.62 / max(dot(neon, vec3(0.2126, 0.7152, 0.0722)), 1e-3);

  float near = smoothstep(0.0, 0.85, r);

  vec3 col = neon * (0.45 + 0.55 * bands) * (0.72 + 0.30 * spokes) * near;
  col += vec3(1.00, 0.90, 0.70) * exp(-fract(iBeat) * 6.0) * 0.10 * near;
  col *= 1.0 - 0.55 * smoothstep(0.35, 0.0, r);

  col *= 0.84 + 0.16 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// ── Neon Horizon variations ──────────────────────────────────────────────────
// Seven takes on the same frame. Each keeps the chunky grid and the horizon line, and
// each changes something structural rather than just the palette: what the floor is made
// of (lines, checker, water, mirrored corridor), what sits on the horizon (sun, moon,
// mountains, nothing), and whether the scheme is dark or light.

// Night storm: no sun. Everything in the frame is quantised to the virtual pixel grid
// EXCEPT the lightning, which is drawn from the raw fragCoord at full output resolution.
// That dichotomy is the point — a smooth, sharp bolt tearing through a hard-edged pixel
// world, rather than a bolt that is just more of the same blocks. It only reads as
// deliberate because there are clouds for it to light up: the flash is what couples the
// two layers together.
neonStorm := """
// Scanline period only. The rest of the frame is sampled at full output resolution —
// unlike its siblings, which quantise every term to a VROWS grid. Quantising here threw
// away most of what a 1080- or 1440-wide render is for: the grid's diagonals came back as
// 12px staircases and the rain as dashes. The scanlines carry the CRT read on their own,
// and being pinned to a row count rather than a pixel count they look the same at any size.
#define SCANROWS 160.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) { s += fft(float(i) / 64.0); }
  return s * 0.125;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Six octaves rather than four. The last two sit below a pixel at 540 wide and only
// resolve once the render is large, which is the point: the cloud gains texture with the
// output size instead of staying a four-octave blur blown up.
float fbm(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 6; i++) {
    s += a * vnoise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return s;
}

// Triangle wave: straight runs meeting at hard corners.
float tri(float x) {
  return abs(fract(x) - 0.5) * 2.0 - 0.5;
}

// The bolt's path down the sky. Deterministic per strike, so it does not crawl mid-flash.
// Built from triangle waves rather than sines — summed sines give a smooth serpentine that
// reads as a glowing rope, where lightning is straight segments meeting at sharp kinks.
//
// Six octaves at halving amplitude and roughly doubling frequency: the same 1/f structure
// as the cloud FBM, but applied to a path instead of a field. That is what makes it read
// as fractal — big deviations carry medium kinks which carry small ones, so the eye finds
// detail at whatever scale it looks, and the bolt gets finer rather than blockier as the
// output resolution goes up.
float boltPath(float y, float s) {
  float v = 0.0;
  float f = 1.7;
  float a = 0.11;
  for (int i = 0; i < 8; i++) {
    v += a * tri(y * f + s * (0.37 + float(i) * 0.61));
    f *= 2.15;
    a *= 0.5;
  }
  return v;
}

// Rain as tapered streaks rather than lit cells. The old version hashed a 140x40 grid and
// filled whole cells, so at 1080 wide every drop was a 7x48px block; these are drawn from
// the position within the cell, so a drop is a hairline whose width follows the pixel size
// and whose tail fades. Two layers at different rates read as depth.
float rain(vec2 uv, float aspect, float t) {
  float acc = 0.0;
  for (int i = 0; i < 2; i++) {
    float fi = float(i);
    float sc = 74.0 + fi * 52.0;
    // Snapping the fall rate puts the drops back at the same position within a cell at the
    // loop point, and wrapping the row index puts them back in the SAME cells — without
    // that second half they are in phase but freshly hashed, which is a whole new field of
    // rain arriving on the seam frame.
    float rate = loopRate(11.0 + fi * 8.0);
    vec2 q = vec2(uv.x * aspect * sc, uv.y * sc * 0.30 + t * rate);
    // Outside the branch below, so the derivative is taken in uniform control flow.
    float w = max(fwidth(q.x) * 0.9, 0.075);
    vec2 cell = floor(q);
    vec2 f = fract(q);
    float h = hash(vec2(cell.x, loopCell(cell.y, rate * iLoopTime)) + fi * 19.0);
    float on = step(0.93, h);
    float len = 0.30 + 0.50 * fract(h * 61.0);
    float x = abs(f.x - 0.5);
    acc += on * smoothstep(w, 0.0, x) * smoothstep(len, 0.0, f.y) * (1.0 - fi * 0.35);
  }
  return acc;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  float b = bass();
  const float horizon = 0.55;

  // Once a bar, not once a beat. A strike on every beat is a 2Hz full-frame flash — the
  // thing the strobe check exists to stop. Real lightning also stutters several times per
  // strike; that is deliberately NOT reproduced here, because it is textbook photosensitive
  // strobe. One clean flash with a decay tail instead.
  float onBar = mod(floor(iBeat), 4.0) < 0.5 ? 1.0 : 0.0;
  // Attack, then decay. An instantaneous onset takes the whole sky from black to full in
  // a single frame — the largest possible frame-to-frame step, and the flash is now
  // lighting clouds across the entire frame rather than a thin bolt, so it counts for far
  // more than it used to. Ramping over ~4 frames keeps the strike sudden to the eye while
  // spreading the luminance change over enough frames to stay clear of the strobe limit.
  float ph = fract(iBeat);
  float strike = onBar * smoothstep(0.0, 0.14, ph) * exp(-ph * 5.0);
  // Which bar we are in, wrapped to the loop so the same sequence of bolts recurs rather
  // than a fresh one arriving at the seam. strike itself is already seamless — it is 0 at
  // every bar line and has decayed to 0.007 by the end of a bar — so this is about the
  // shape being continuous across the join, not the flash.
  float bars = floor(iBeat * 0.25);
  float loopBars = floor(iLoopBeats * 0.25 + 0.5);
  float seed = iLoopBeats > 0.0 ? mod(bars, max(1.0, loopBars)) : bars;

  vec2 rp = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
  // One pixel in the bolt's coordinate space, used to floor every line width below so a
  // stroke thins with the output size but never falls between samples and starts flickering.
  float aa = max(fwidth(rp.x), 1e-5);

  // Main channel, plus branches that fork off it at hashed heights. Each branch leaves the
  // trunk at its fork point, diverges as it descends, carries its own smaller jitter and
  // tapers to nothing at its tip — so the silhouette keeps splitting the way a real
  // discharge does rather than being one line with one spur.
  float boltCore = 0.0;
  float boltGlow = 0.0;

  float dx = abs(rp.x - boltPath(uv.y, seed));
  boltCore = smoothstep(max(0.0018, aa), 0.0, dx);
  boltGlow = exp(-dx * 34.0);

  for (int i = 0; i < 7; i++) {
    float fi = float(i);
    float h = hash(vec2(seed, fi));
    float yFork = 0.34 + 0.62 * h;
    float len = 0.09 + 0.20 * hash(vec2(seed, fi + 40.0));
    float t = (yFork - uv.y) / len;
    // Outside this branch's run of the sky, so it contributes nothing here.
    if (t < 0.0 || t > 1.0) { continue; }

    float side = hash(vec2(seed, fi + 20.0)) < 0.5 ? -1.0 : 1.0;
    float spread = 0.55 + 0.85 * hash(vec2(seed, fi + 60.0));
    float bx = boltPath(yFork, seed)
             + side * t * len * spread
             + 0.024 * tri(uv.y * 7.0 + fi * 2.3)
             + 0.011 * tri(uv.y * 17.0 + fi * 5.1);

    float d = abs(rp.x - bx);
    float taper = 1.0 - t;
    boltCore = max(boltCore, smoothstep(max(0.0013 * taper, aa), 0.0, d) * taper);
    boltGlow += exp(-d * 62.0) * taper * 0.45;
  }
  boltGlow = min(boltGlow, 2.5);

  vec3 col;
  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
    col = mix(vec3(0.07, 0.08, 0.16), vec3(0.02, 0.02, 0.06), smoothstep(0.0, 0.40, p.y));

    // Clouds, drifting slowly and densest well above the horizon. Domain-warped: the field
    // is displaced by a slower copy of itself, which bends the noise into billows and
    // wisps instead of the isotropic mottle plain FBM gives.
    vec2 cp = vec2(uv.x * aspect * 3.0, (uv.y - horizon) * 5.0) + loopDrift(vec2(0.04, 0.015), iTime);
    float warp = fbm(cp * 0.55);
    float n = fbm(cp + vec2(warp, warp * 0.6) * 0.85);
    // A fine layer over the top, for edge detail that only exists at large output sizes.
    // Its own drift rather than cp's, because it is sampled at 4.7x and would otherwise
    // move 4.7x as fast; scaling cp's drift is still periodic, just wrong in speed.
    n = n * 0.84 + fbm(cp * 4.7 + loopDrift(vec2(0.07, 0.0), iTime)) * 0.16;
    float density = smoothstep(0.34, 0.68, n) * smoothstep(0.0, 0.14, uv.y - horizon);
    col = mix(col, mix(vec3(0.05, 0.06, 0.12), vec3(0.16, 0.17, 0.27), n), density);

    // Lit from the bolt, falling off with distance from its channel, so the cloud that the
    // strike passes through blows out and the far side stays dark.
    float lit = strike * exp(-abs(p.x - boltPath(uv.y, seed)) * 4.0);
    col += vec3(0.50, 0.58, 0.85) * lit * density * 1.35;
    // Ambient bounce, so the whole sky lifts a little rather than only the cloud.
    col += vec3(0.16, 0.20, 0.38) * strike * 0.20;

    col += vec3(0.35, 0.45, 0.60) * rain(uv, aspect, iTime) * 0.60;

    // The bolt goes on last and unquantised: hard cores with a soft halo either side.
    col += vec3(0.85, 0.92, 1.00) * boltCore * strike;
    col += vec3(0.40, 0.52, 0.95) * boltGlow * strike * 0.45;
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = (uv.x - 0.5) * z * aspect * 8.0;
    // Sampled through fract(), so the floor already repeats every cell and only the scroll
    // rate has to be nudged to fit a whole number of cells into the loop.
    float zw = z * 2.0 + loopPhase(2.0, iTime);

    // A solid filament with exactly one pixel of AA either side, plus a separate low halo.
    // Ramping a single smoothstep across the whole line width — which is what this was —
    // leaves no flat centre at all, so every line is pure gradient and the floor reads
    // soft however many pixels it is drawn with. Splitting the two lets the core stay hard
    // while the glow stays wide, which is the actual shape of a lit tube.
    //
    // Each is scaled back once a pixel no longer fits inside it, so a line that has shrunk
    // below the sample spacing dims out instead of aliasing into moiré. That compensation
    // is what lets the grid be drawn at full resolution at all: quantising to a row grid
    // was hiding the same problem, at the cost of staircased diagonals.
    float gx = abs(fract(xw) - 0.5);
    float gz = abs(fract(zw) - 0.5);
    float ax = max(fwidth(xw) * 0.5, 1e-6);
    float az = max(fwidth(zw) * 0.5, 1e-6);

    const float coreW = 0.026;
    float core = max(smoothstep(coreW + ax, max(coreW - ax, 0.0), gx) * min(1.0, coreW / ax),
                     smoothstep(coreW + az, max(coreW - az, 0.0), gz) * min(1.0, coreW / az));

    const float haloW = 0.085;
    float halo = max(smoothstep(haloW, 0.0, gx) * min(1.0, haloW / ax),
                     smoothstep(haloW, 0.0, gz) * min(1.0, haloW / az));

    float line = min(core + halo * 0.30, 1.15);

    // A quarter-spaced sub-grid on the near floor only, held to the same rule. Faint, and
    // gone by mid-distance: at full strength across the whole floor it reads as graph
    // paper rather than as a horizon. It is here so the foreground has something left to
    // resolve at 1440 that it did not have at 540.
    const float subW = 0.055;
    float sx = max(ax * 4.0, 1e-6);
    float sz = max(az * 4.0, 1e-6);
    float sub = max(smoothstep(subW + sx, max(subW - sx, 0.0), abs(fract(xw * 4.0) - 0.5)) * min(1.0, subW / sx),
                    smoothstep(subW + sz, max(subW - sz, 0.0), abs(fract(zw * 4.0) - 0.5)) * min(1.0, subW / sz));
    line = max(line, sub * 0.16 * smoothstep(0.08, 0.34, depth));

    float settle = smoothstep(0.0, 0.09, depth);
    vec3 grid = mix(vec3(0.30, 0.45, 0.85), vec3(0.10, 0.15, 0.45), clamp(depth * 2.2, 0.0, 1.0));
    col = vec3(0.01, 0.01, 0.04) + grid * line * settle * (0.50 + b * 1.2 + strike * 0.9);
  }

  // Scanlines skip the bolt — running them over it would re-chunk the one smooth thing.
  // Pinned to SCANROWS rather than to a pixel count, so the banding keeps the same
  // thickness relative to the frame however large the render is.
  float scanCell = iResolution.y / SCANROWS;
  float scan = 0.82 + 0.18 * step(0.5, fract(fragCoord.y / (scanCell * 2.0)));
  col *= mix(scan, 1.0, clamp(boltCore * strike, 0.0, 1.0));

  fragColor = vec4(col, 1.0);
}
"""

// Daybreak: the palette inverted. Pale floor, dark lines, sun coming up — the one light
// scheme in the set, so it is registered with the light themeKey.
neonDaybreak := """
#define VROWS 160.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) { s += fft(float(i) / 64.0); }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  float b = bass();
  float beat = exp(-fract(iBeat) * 4.0);
  const float horizon = 0.58;

  vec3 col;
  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
    col = mix(vec3(1.00, 0.86, 0.62), vec3(0.60, 0.79, 0.98), smoothstep(0.0, 0.42, p.y));

    float radius = 0.16 + 0.02 * b;
    float d = length(p - vec2(0.0, 0.05));
    float disc = step(d, radius);
    float across = (p.y - 0.05 + radius) / (2.0 * radius);
    float bands = step(0.35, fract(across * 14.0));
    float cut = mix(1.0, bands, smoothstep(0.90, 0.20, across));
    vec3 sunCol = mix(vec3(1.00, 0.60, 0.32), vec3(1.00, 0.99, 0.92), clamp(across, 0.0, 1.0));
    col = mix(col, sunCol, disc * cut);
    col += vec3(1.00, 0.80, 0.55) * exp(-d * 5.0) * 0.22;
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = (uv.x - 0.5) * z * aspect * 8.0;
    float zw = z * 2.0 + loopPhase(2.0, iTime);
    float gx = abs(fract(xw) - 0.5);
    float gz = abs(fract(zw) - 0.5);
    float weight = 0.055 + 0.03 * beat;
    float line = max(smoothstep(weight, 0.0, gx), smoothstep(weight, 0.0, gz));
    float settle = smoothstep(0.0, 0.09, depth);

    // Dark lines ON a pale floor, the exact inverse of every other variation here.
    vec3 floorCol = mix(vec3(0.93, 0.95, 0.96), vec3(0.70, 0.83, 0.88), clamp(depth * 2.0, 0.0, 1.0));
    vec3 lineCol = mix(vec3(0.05, 0.32, 0.42), vec3(0.35, 0.10, 0.30), clamp(depth * 2.0, 0.0, 1.0));
    col = mix(floorCol, lineCol, line * settle * (0.65 + b * 0.35));
  }

  col *= 0.94 + 0.06 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Moonlit water: the floor becomes a sea, and the sun's banded disc becomes a moon with a
// shimmering reflection column driven by the waveform rather than the spectrum.
neonOcean := """
#define VROWS 150.0

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  const float horizon = 0.60;

  vec3 col;
  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
    col = mix(vec3(0.06, 0.09, 0.22), vec3(0.01, 0.01, 0.06), smoothstep(0.0, 0.40, p.y));

    float star = step(0.995, hash(floor(px / cell) + 11.0));
    col += vec3(0.70, 0.80, 1.00) * star * 0.7;

    float d = length(p - vec2(0.0, 0.16));
    float moon = step(d, 0.11);
    // Craters instead of bands: a moon that is banded reads as a sun in the wrong palette.
    float crater = step(0.72, hash(floor(px / (cell * 2.0)) + 5.0));
    col = mix(col, mix(vec3(0.92, 0.94, 0.88), vec3(0.74, 0.78, 0.76), crater), moon);
    col += vec3(0.55, 0.65, 0.90) * exp(-d * 9.0) * 0.30;
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);

    // Swell: bands of water rolling toward the viewer, no grid lines anywhere.
    float swell = sin(z * 3.0 - iTime * loopRateAngular(1.6)) * 0.5
                + sin(z * 7.3 + iTime * loopRateAngular(1.1)) * 0.25;
    float crest = 0.5 - 0.5 * cos(6.2831853 * fract(z * 1.4 - iTime * loopRate(0.8) + swell * 0.15));
    float settle = smoothstep(0.0, 0.07, depth);

    vec3 sea = mix(vec3(0.05, 0.16, 0.34), vec3(0.01, 0.04, 0.12), clamp(depth * 2.0, 0.0, 1.0));
    col = sea + vec3(0.20, 0.42, 0.62) * crest * settle * 0.55;

    // Reflection column under the moon, chopped up by the waveform.
    float shimmer = texture(iChannel0, vec2(fract(z * 0.25 + iTime * loopRate(0.15)), 0.0)).z;
    float column = smoothstep(0.20, 0.0, abs(uv.x - 0.5) * aspect);
    col += vec3(0.85, 0.90, 1.00) * column * crest * settle * (0.25 + shimmer * 0.85) * 0.7;
  }

  col *= 0.86 + 0.14 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Ridgeline: layered mountain silhouettes stacked on the horizon, their peaks pushed up
// by the spectrum. The floor grid survives, but it is no longer what you look at.
neonRidgeline := """
#define VROWS 160.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float ridge(float x, float s) {
  return sin(x * 5.0 + s) * 0.5 + sin(x * 11.0 + s * 3.1) * 0.25 + sin(x * 23.0 + s * 7.7) * 0.12;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  const float horizon = 0.42;

  float wx = (uv.x - 0.5) * aspect;
  vec3 col;

  if (uv.y > horizon) {
    vec2 p = vec2(wx, uv.y - horizon);
    col = mix(vec3(0.28, 0.06, 0.34), vec3(0.03, 0.01, 0.10), smoothstep(0.0, 0.50, p.y));

    float radius = 0.22;
    float d = length(p - vec2(0.0, 0.22));
    float across = (p.y - 0.22 + radius) / (2.0 * radius);
    float bands = step(0.45, fract(across * 10.0));
    float cut = mix(1.0, bands, smoothstep(0.85, 0.15, across));
    vec3 sunCol = mix(vec3(1.00, 0.25, 0.45), vec3(1.00, 0.82, 0.30), clamp(across, 0.0, 1.0));
    col = mix(col, sunCol, step(d, radius) * cut);

    // Three layers, nearest last so it paints over the ones behind it.
    for (int k = 2; k >= 0; k--) {
      float fk = float(k);
      float band = fft(0.05 + fk * 0.22);
      float h = 0.045 + fk * 0.052 + 0.045 * ridge(wx * (1.0 + fk * 0.6), fk * 13.0) + band * 0.05;
      if (p.y < h) {
        vec3 rock = mix(vec3(0.16, 0.04, 0.26), vec3(0.05, 0.01, 0.10), fk * 0.5);
        col = rock;
        // Lit rim on each ridge line so the layers separate against the sky.
        float rim = smoothstep(cell / iResolution.y * 2.5, 0.0, abs(p.y - h));
        col += mix(vec3(1.00, 0.45, 0.55), vec3(0.45, 0.85, 1.00), fk * 0.5) * rim * 0.8;
      }
    }
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = wx * z * 8.0;
    float zw = z * 2.0 + loopPhase(2.0, iTime);
    float gx = abs(fract(xw) - 0.5);
    float gz = abs(fract(zw) - 0.5);
    float line = max(smoothstep(0.06, 0.0, gx), smoothstep(0.06, 0.0, gz));
    float settle = smoothstep(0.0, 0.09, depth);
    vec3 grid = mix(vec3(0.90, 0.25, 0.55), vec3(0.35, 0.08, 0.45), clamp(depth * 2.2, 0.0, 1.0));
    col = vec3(0.02, 0.00, 0.05) + grid * line * settle * 0.9;
  }

  col *= 0.84 + 0.16 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Vapor: pastel, checkerboard floor instead of a wire grid, and a soft wide-banded sun.
vaporChecker := """
#define VROWS 150.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  float b = fft(0.03);
  const float horizon = 0.60;

  vec3 col;
  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
    col = mix(vec3(0.98, 0.72, 0.82), vec3(0.55, 0.72, 0.94), smoothstep(0.0, 0.40, p.y));

    float star = step(0.993, hash(floor(px / cell) + 3.0));
    col = mix(col, vec3(1.0), star * 0.8);

    float radius = 0.21 + 0.015 * b;
    float d = length(p - vec2(0.0, 0.12));
    float across = (p.y - 0.12 + radius) / (2.0 * radius);
    float bands = step(0.40, fract(across * 5.0));
    float cut = mix(1.0, bands, smoothstep(0.80, 0.20, across));
    vec3 sunCol = mix(vec3(1.00, 0.62, 0.86), vec3(1.00, 0.93, 0.78), clamp(across, 0.0, 1.0));
    col = mix(col, sunCol, step(d, radius) * cut);
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = (uv.x - 0.5) * z * aspect * 5.0;
    // Cells alternate, so the pattern repeats every TWO of them, not every one — the rate
    // is snapped in units of a two-cell pair or the floor comes back around inverted.
    float zw = z * 1.6 + loopPhase(1.6, iTime, 2.0);

    // Filled squares rather than lines — the floor reads as a surface, not a wireframe.
    float check = mod(floor(xw) + floor(zw), 2.0);
    float settle = smoothstep(0.0, 0.08, depth);
    vec3 a = mix(vec3(0.85, 0.55, 0.80), vec3(0.30, 0.16, 0.42), clamp(depth * 1.8, 0.0, 1.0));
    vec3 c = mix(vec3(0.55, 0.85, 0.92), vec3(0.14, 0.26, 0.46), clamp(depth * 1.8, 0.0, 1.0));
    col = mix(vec3(0.06, 0.03, 0.12), mix(a, c, check), settle);
  }

  col *= 0.88 + 0.12 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Corridor: the floor mirrored about the horizon, so the grid closes overhead and the
// frame becomes a passage rather than a landscape.
neonCorridor := """
#define VROWS 160.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) { s += fft(float(i) / 64.0); }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  float b = bass();
  const float horizon = 0.50;

  // Mirrored: floor below, ceiling above, both converging on the same vanishing line.
  float depth = abs(uv.y - horizon);
  float z = 0.26 / (depth + 0.045);
  float xw = (uv.x - 0.5) * z * aspect * 7.0;
  // Bass shoves the floor forward rather than speeding it up. A rate of (2.2 + b * 1.4)
  // cannot loop at any length: the phase is iTime * b, and while b itself repeats with the
  // music, its product with a clock that only ever goes up does not — the accumulated
  // distance at the loop point depends on everything the bass did in between. As a
  // displacement it is bounded, periodic because b is, and still pulls on every hit.
  float zw = z * 2.0 + loopPhase(2.2, iTime) + b * 0.35;

  float gx = abs(fract(xw) - 0.5);
  float gz = abs(fract(zw) - 0.5);
  float line = max(smoothstep(0.055, 0.0, gx), smoothstep(0.055, 0.0, gz));
  float settle = smoothstep(0.0, 0.075, depth);

  float ceiling = uv.y > horizon ? 1.0 : 0.0;
  vec3 tint = mix(vec3(0.20, 0.95, 1.00), vec3(0.85, 0.30, 1.00), ceiling);

  vec3 col = vec3(0.01, 0.01, 0.04) + tint * line * settle * (0.75 + b * 1.2);

  // Glow at the vanishing line, so the passage has somewhere to be going.
  col += mix(vec3(0.30, 0.85, 1.00), vec3(0.90, 0.40, 1.00), ceiling)
       * exp(-depth * 26.0) * (0.30 + 0.35 * exp(-fract(iBeat) * 4.0));

  col *= 0.82 + 0.18 * step(0.5, fract(px.y / (cell * 2.0)));
  fragColor = vec4(col, 1.0);
}
"""

// Amber CRT: one phosphor colour, wireframe sun, heavy scanlines. Closer to a terminal
// than to a sunset, which is the point.
amberCrt := """
#define VROWS 140.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) { s += fft(float(i) / 64.0); }
  return s * 0.125;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 px = (floor(fragCoord / cell) + 0.5) * cell;
  vec2 uv = px / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  float b = bass();
  const float horizon = 0.58;

  // One channel of brightness the whole way through; colour is applied once at the end.
  float lum = 0.0;

  if (uv.y > horizon) {
    vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - horizon);
    float d = length(p - vec2(0.0, 0.13));
    float radius = 0.19 + 0.02 * b;
    // Outline, not a disc: a filled sun in one colour is just a blob.
    lum += smoothstep(0.012, 0.0, abs(d - radius)) * 0.95;
    // A few horizontal rules across it, the way a vector display would draw it.
    lum += step(d, radius) * step(0.80, fract((p.y - 0.13) * 26.0)) * 0.55;
  } else {
    float depth = horizon - uv.y;
    float z = 0.30 / (depth + 0.05);
    float xw = (uv.x - 0.5) * z * aspect * 6.0;
    float zw = z * 1.8 + loopPhase(1.8, iTime);
    float gx = abs(fract(xw) - 0.5);
    float gz = abs(fract(zw) - 0.5);
    lum += max(smoothstep(0.045, 0.0, gx), smoothstep(0.045, 0.0, gz))
         * smoothstep(0.0, 0.09, depth) * (0.70 + b * 0.8);
  }

  // Phosphor: amber, with the bright end pushed toward white the way a hot CRT blooms.
  vec3 col = vec3(1.00, 0.62, 0.16) * lum + vec3(1.00, 0.95, 0.80) * smoothstep(0.75, 1.30, lum) * 0.5;
  col += vec3(0.06, 0.025, 0.005);

  // Heavier scanlines than the others, and a vignette, for the glass.
  col *= 0.62 + 0.38 * step(0.5, fract(px.y / (cell * 2.0)));
  vec2 q = (uv - 0.5) * vec2(aspect, 1.0);
  col *= 1.0 - 0.55 * smoothstep(0.35, 0.85, length(q));
  fragColor = vec4(col, 1.0);
}
"""

// Flat keying green, for external compositing: render the notes/staff over this, then
// chromakey the green out in ffmpeg and overlay on any backdrop (e.g. a Milkdrop
// visualization). Not a visual — deliberately ignores every uniform, so it is trivially
// loop-safe and check-shaders' seam test passes by construction.
chromaKey := """
void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  fragColor = vec4(0.0, 1.0, 0.0, 1.0);
}
"""


// ── Spectrum Skyline directions (preview candidates, not in the preset list) ──
// Six takes on the same data, to be judged side by side at /sheet. All read the
// spectrum the same corrected way: half-octave bars averaged across their span.

skylineMirror := """
#define HORIZON 0.40
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float i = floor(uv.x * BARS);
  float e = level(i);
  float h = 0.05 + pow(e, 0.65) * 0.30;

  vec3 col = mix(vec3(0.04, 0.01, 0.10), vec3(0.28, 0.05, 0.34), uv.y);
  vec3 tint = mix(vec3(0.20, 0.95, 1.00), vec3(1.00, 0.20, 0.75), step(0.5, fract(i * 0.37)));

  // The city stands above HORIZON and is redrawn upside down below it, so the bottom of
  // the frame — the part the staff never covers — carries the spectrum a second time.
  float y = uv.y - HORIZON;
  float sky = step(0.0, y);
  // A plain iTime term would not return at the loop seam; loopPhase fits whole cycles.
  float ripple = sin(uv.x * 40.0 + loopPhase(0.35, iTime) * 6.2831853) * 0.007;
  // Foreshortened, the way a reflection on water actually reads.
  float hh = mix(-y * 0.72 + ripple, y, sky);

  float inside = step(0.0, hh) * step(hh, h);
  float fade = mix(exp(y * 7.0) * 0.6, 1.0, sky);
  col = mix(col, tint * (0.22 + e * 0.9), inside * fade);

  float beat = exp(-fract(iBeat) * 5.0);
  float rim = smoothstep(0.007, 0.0, abs(hh - h)) * step(0.0, hh);
  col += tint * rim * (0.8 + beat * 1.2) * fade;

  fragColor = vec4(col, 1.0);
}
"""

skylineDepth := """
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

// One row per depth. Far rows are shorter, dimmer and bluer, so the whole spectrum is on
// screen at once instead of competing for the same fourteen slots.
// One depth plane, composited OVER what is already there so nearer rows hide farther ones.
vec3 layer(vec3 under, vec2 uv, float depth, float bars, float baseY, float scale,
           vec3 tint, float beat, float bright) {
  float i = floor(uv.x * bars);
  // Each row reads its own third of the spectrum: lows in front, highs at the back.
  float e = level(clamp(i / bars * BARS * 0.34 + depth * BARS * 0.33, 0.0, BARS - 1.0));
  float h = baseY + pow(e, 0.65) * scale;
  float inside = step(uv.y, h);
  float edge = smoothstep(0.0, 0.10, abs(fract(uv.x * bars) - 0.5));
  vec3 face = tint * (0.16 + 0.34 * edge + e * 0.45) * bright;
  float rim = smoothstep(0.005, 0.0, abs(uv.y - h));
  vec3 col = mix(under, face, inside);
  return col + tint * rim * (0.7 + beat * 0.9) * bright;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float beat = exp(-fract(iBeat) * 5.0);

  vec3 col = mix(vec3(0.03, 0.01, 0.09), vec3(0.24, 0.04, 0.32), uv.y);
  // Back to front with real occlusion. Adding the rows instead let three translucent
  // layers pile up into one pink mass with no depth in it at all.
  col = layer(col, uv, 2.0, 22.0, 0.16, 0.16, vec3(0.22, 0.34, 0.70), beat, 0.45);
  col = layer(col, uv, 1.0, 16.0, 0.09, 0.24, vec3(0.45, 0.28, 0.85), beat, 0.72);
  col = layer(col, uv, 0.0, 10.0, 0.02, 0.34, vec3(1.00, 0.35, 0.70), beat, 1.00);

  fragColor = vec4(col, 1.0);
}
"""

skylineRadial := """
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  // Centred low, so the burst fills the bottom of the frame and clears the staff.
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.30);
  float r = length(p);
  float beat = exp(-fract(iBeat) * 5.0);

  vec3 col = mix(vec3(0.04, 0.01, 0.11), vec3(0.26, 0.03, 0.30), uv.y);

  // Spectrum around the circle rather than across the frame. Mirrored on |angle| so the
  // loud low bins and near-silent high bins never sit next to each other as a seam.
  float ang = abs(atan(p.y, p.x + 1e-8)) * 0.31830989;
  float i = floor(ang * BARS);
  float e = level(i);

  // Each spoke is a bar laid on its side, growing out of the sun.
  float reach = 0.12 + pow(e, 0.65) * 0.40;
  float spoke = smoothstep(0.02, 0.0, abs(fract(ang * BARS) - 0.5) - 0.34);
  vec3 tint = mix(vec3(0.20, 0.95, 1.00), vec3(1.00, 0.35, 0.55), fract(i * 0.37));
  col += tint * spoke * step(r, reach) * (0.25 + e * 1.1);

  // The sun itself, banded the way the original's is.
  float disc = step(r, 0.16);
  float bands = step(0.45, fract((p.y + 0.16) / 0.32 * 7.0));
  col = mix(col, mix(vec3(1.00, 0.25, 0.50), vec3(1.00, 0.80, 0.30), clamp((p.y + 0.16) / 0.32, 0.0, 1.0)),
            disc * bands);
  col += vec3(1.00, 0.30, 0.50) * exp(-r * 6.0) * (0.16 + beat * 0.22);

  fragColor = vec4(col, 1.0);
}
"""

skylineScope := """
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float i = floor(uv.x * BARS);
  float e = level(i);
  float h = 0.06 + pow(e, 0.65) * 0.30;
  float beat = exp(-fract(iBeat) * 5.0);

  vec3 col = mix(vec3(0.04, 0.01, 0.10), vec3(0.24, 0.04, 0.30), uv.y);

  vec3 face = mix(vec3(0.09, 0.03, 0.18), vec3(0.16, 0.06, 0.28), uv.y / max(h, 1e-4));
  float inside = step(uv.y, h);
  col = mix(col, face, inside);

  // The time-domain half of iChannel0 — .z/.w, which this shader never used — drawn as a
  // neon scope riding just above the rooflines. Centred on 0.5 for silence.
  vec4 a = texture(iChannel0, vec2(uv.x, 0.0));
  float l = a.z - 0.5;
  float rr = a.w - 0.5;
  // High in the frame, not centred: the staff scrolls across the middle, so a scope at 0.5
  // is drawn where nobody can see it. The top quarter and the bottom third are the only
  // parts of a short that are reliably clear, and this shader uses both.
  float mid = 0.82;
  float dl = abs(uv.y - (mid + l * 0.30));
  float dr = abs(uv.y - (mid + rr * 0.30));
  const float glow = 0.004;
  col += vec3(0.20, 0.80, 1.00) * glow / (dl + glow);
  col += vec3(1.00, 0.30, 0.70) * glow / (dr + glow);

  vec3 tint = mix(vec3(0.20, 0.95, 1.00), vec3(1.00, 0.20, 0.75), step(0.5, fract(i * 0.37)));
  float rim = smoothstep(0.006, 0.0, abs(uv.y - h));
  col += tint * rim * (0.9 + beat * 1.1);

  fragColor = vec4(col, 1.0);
}
"""

skylineGrid := """
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float beat = exp(-fract(iBeat) * 5.0);
  vec3 col = mix(vec3(0.03, 0.01, 0.09), vec3(0.20, 0.03, 0.28), uv.y);

  // Floor below the horizon, receding. 1/y is the perspective divide; the grid scrolls
  // toward the viewer through loopPhase so it lands whole at the seam.
  float horizon = 0.46;
  if (uv.y < horizon) {
    float d = max(horizon - uv.y, 1e-3);
    float z = 0.06 / d;
    float scroll = loopPhase(0.5, iTime);
    // Line width has to shrink with depth or the near rows fuse into a solid sheet — which
    // is what made this a cyan wash rather than a grid. Scaling the threshold by d keeps
    // every row about one pixel wide however close it is.
    // `abs(fract(x) - 0.5)` is 0 at a cell's CENTRE and 0.5 at its boundary, so a line
    // lives where that value approaches 0.5. The earlier form tested the other end of the
    // range and filled the cell interiors instead — which is why the floor came out as a
    // solid cyan sheet with thin gaps rather than a grid.
    float wr = clamp(d * 1.6, 0.012, 0.45);
    float lines = smoothstep(0.5 - wr, 0.5, abs(fract(z + scroll) - 0.5));
    // Columns keep constant WORLD spacing and converge on the vanishing point once
    // divided by depth.
    float fan = (uv.x - 0.5) / d * 0.30;
    float wc = clamp(d * 1.6, 0.012, 0.45);
    float cols = smoothstep(0.5 - wc, 0.5, abs(fract(fan) - 0.5));
    float fog = exp(-d * 3.0);
    col += vec3(0.30, 0.95, 1.00) * max(lines, cols) * fog * 0.80;
  }

  // Bars rise from the horizon line itself.
  float i = floor(uv.x * BARS);
  float e = level(i);
  float h = horizon + pow(e, 0.65) * 0.34;
  float inside = step(uv.y, h) * step(horizon, uv.y);
  float edge = smoothstep(0.0, 0.12, abs(fract(uv.x * BARS) - 0.5));
  vec3 tint = mix(vec3(0.30, 0.95, 1.00), vec3(1.00, 0.25, 0.70), step(0.5, fract(i * 0.37)));
  col += tint * inside * (0.14 + edge * 0.30 + e * 0.7);
  col += tint * smoothstep(0.006, 0.0, abs(uv.y - h)) * step(horizon, uv.y) * (0.9 + beat * 1.1);

  fragColor = vec4(col, 1.0);
}
"""

skylineBloom := """
#define BARS 14.0
// The span the bars cover, as a fraction of Nyquist: roughly 55 Hz to 6 kHz.
#define FLO 0.0023
#define FHI 0.2500

// Mean level across one bar's whole frequency span, rather than one texel out of 512.
float band(float lo, float hi) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(lo, hi, (float(k) + 0.5) * 0.125);
    vec2 f = texture(iChannel0, vec2(clamp(t, 0.0, 1.0), 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  return s * 0.125;
}

// Bar i's level: half-octave wide, tilted to offset the natural high-frequency rolloff.
float level(float i) {
  float lo = FLO * pow(FHI / FLO, i / BARS);
  float hi = FLO * pow(FHI / FLO, (i + 1.0) / BARS);
  float e = band(lo, hi) * (1.0 + 1.6 * (i / (BARS - 1.0)));
  return clamp(e, 0.0, 1.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 uv = fragCoord / iResolution.xy;
  float beat = exp(-fract(iBeat) * 5.0);
  vec3 col = mix(vec3(0.02, 0.01, 0.07), vec3(0.16, 0.03, 0.26), uv.y);

  // No silhouette at all: each band is a shaft of light standing on the floor, so the
  // spectrum reads as colour and reach instead of as a wall of buildings.
  for (int k = 0; k < 14; k++) {
    float i = float(k);
    float e = level(i);
    float cx = (i + 0.5) / BARS;
    float reach = 0.05 + pow(e, 0.65) * 0.52;
    // Narrow core, wide halo — the halo is what survives being scaled down to a feed.
    float dx = abs(uv.x - cx);
    float shaft = smoothstep(0.030, 0.0, dx) * smoothstep(reach, reach * 0.25, uv.y);
    float halo = smoothstep(0.085, 0.0, dx) * smoothstep(reach * 1.3, 0.0, uv.y) * 0.35;
    vec3 tint = mix(vec3(0.25, 0.85, 1.00), vec3(1.00, 0.30, 0.65), fract(i * 0.37));
    col += tint * (shaft * (0.35 + e * 1.3) + halo * (0.5 + e));
  }

  // Floor line the shafts stand on, pulsed by the beat.
  col += vec3(0.85, 0.95, 1.00) * smoothstep(0.010, 0.0, uv.y) * (0.5 + beat * 0.9);

  fragColor = vec4(col, 1.0);
}
"""

// ── quiet: the staff is the picture ─────────────────────────────────────────
// Stated 2026-09-28: the subtler backdrops let the staff keep the focus. The staff is a
// near-opaque light panel across the middle ~55% of the frame, and notes spill above and
// below it, so these keep the middle dark and flat and put all the motion at the frame's
// top and bottom edges. Low contrast throughout: nothing here is brighter than a note.
//
// Crisp on purpose: everything is quantised to a virtual pixel grid, and beat accents
// SNAP on at the beat and fall away in a straight line, with no ease in. (No hold at
// the top either: check-shaders.mjs wants motion from the very first frame.)

// LED spectrum meters standing on the bottom edge and hanging from the top one. Left
// channel below, right channel above, bass in the middle of each row of bars.
quietMeters := """
// 160 rows: a whole number of device pixels per cell at every delivery height (6 at 960,
// 8 at 1280, 12 at 1920, 16 at 2560). A fractional cell alternates 13- and 14-pixel rows,
// which on evenly spaced LED segments reads as uneven spacing.
#define VROWS 160.0
#define BARS 18.0
// Segment height in rows: two lit, one gap.
#define SEG 3.0
#define SEGS 10.0

// t 0..1 across the bars, spread log-frequency from 40 Hz to 5 kHz: where the notes are.
// A power curve over the whole 0..Nyquist range spends half the bars above 5 kHz, which
// on a General MIDI render is silence. Nyquist is taken as 24 kHz (renders are 48 kHz):
// iSampleRate is declared in the header but nothing sets it.
float chan(float t, float right) {
  float hz = 40.0 * pow(125.0, clamp(t, 0.0, 1.0));
  vec2 f = texture(iChannel0, vec2(hz / 24000.0, 0.0)).xy;
  return mix(f.x, f.y, right);
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 ip = floor(fragCoord / cell);
  float vcols = floor(iResolution.x / cell);
  vec2 uv = (ip + 0.5) * cell / iResolution.xy;

  // Ground: flat steps from the edges inward, darkest behind the staff.
  float edge = abs(uv.y - 0.5) * 2.0;
  float g = floor(edge * 6.0) / 6.0;
  vec3 col = mix(vec3(0.014, 0.016, 0.036), vec3(0.046, 0.040, 0.092), g * g);

  float top = step(0.5, uv.y);
  float r = mix(ip.y, VROWS - 1.0 - ip.y, top);

  // Whole cells per bar, one of them the gap, and the row of bars centred: every bar the
  // same width at every output size, whatever is left over split between the two sides.
  float bw = max(2.0, floor(vcols / BARS));
  float x = ip.x - floor((vcols - bw * BARS) * 0.5);
  float bi = floor(x / bw);
  float inBar = step(0.0, x) * step(bi, BARS - 1.0) * step(x - bi * bw, bw - 2.0);
  float t = abs((bi + 0.5) / BARS - 0.5) * 2.0;

  // The analyser's bytes are dB over roughly -100..-30, and the shorts are rendered with a
  // gain trim (often -24 dB), so a loud passage sits around 0.5. Stretch the part of the
  // range music actually uses, and tilt it up toward the treble, which is always quieter,
  // so the outer bars move too.
  float e = clamp((chan(t, top) - 0.22) / 0.45 + t * 0.25, 0.0, 1.0);

  // Heights in whole segments, so no block is ever cut short, and the cap is exactly the
  // top one.
  float n = floor(e * SEGS + 0.5);
  float si = floor(r / SEG);
  float lit = inBar * step(mod(r, SEG), SEG - 1.5) * step(si, n - 1.0);
  float cap = lit * step(n - 1.0, si);

  vec3 tint = mix(vec3(0.10, 0.30, 0.36), vec3(0.28, 0.15, 0.42), t);

  float ph = fract(iBeat);
  float down = step(mod(floor(iBeat), 4.0), 0.5);
  float hit = 1.0 - clamp(ph / 0.5, 0.0, 1.0);
  vec3 accent = mix(vec3(0.62, 0.78, 0.90), vec3(0.95, 0.62, 0.25), down);

  col = mix(col, tint * 0.75, lit);
  col = mix(col, mix(tint * 1.5, accent * 0.7, hit), cap);

  // A one-cell floor under each bar, which carries the beat when the bars are low.
  float floorRow = inBar * step(r, 0.5) * (1.0 - lit);
  col = mix(col, mix(tint * 0.6, accent * 0.55, hit), floorRow);

  fragColor = vec4(col, 1.0);
}
"""

// A night sky with the galaxy across it: a tilted band of denser, fainter stars over a
// haze, split by a dark dust lane, the way the Milky Way looks from somewhere dark. The
// haze is ordered-dithered pixels rather than a blur. Each star listens to its own band of
// the spectrum; a beat lights a quarter of the bright ones in turn, so the field takes
// four beats to go all the way round.
stillStars := """
// 320 rows: whole device pixels per cell at 960, 1280, 1920 and 2560.
#define VROWS 320.0

// Integer-style hashes (Hoskins, "hash without sine"). The sin() hash leaves visible rows
// and diagonals at these cell counts, which reads as stars on a lattice.
float hash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  return noise(p) * 0.55 + noise(p * 2.03 + 17.0) * 0.30 + noise(p * 4.1 + 41.0) * 0.15;
}

float bayer4(vec2 p) {
  vec2 q = mod(p, 4.0);
  float i = q.x + q.y * 4.0;
  return (i < 1.0 ? 0.0 : i < 2.0 ? 8.0 : i < 3.0 ? 2.0 : i < 4.0 ? 10.0
       : i < 5.0 ? 12.0 : i < 6.0 ? 4.0 : i < 7.0 ? 14.0 : i < 8.0 ? 6.0
       : i < 9.0 ? 3.0 : i < 10.0 ? 11.0 : i < 11.0 ? 1.0 : i < 12.0 ? 9.0
       : i < 13.0 ? 15.0 : i < 14.0 ? 7.0 : i < 15.0 ? 13.0 : 5.0) / 16.0;
}

float fft(float t) {
  float hz = 40.0 * pow(125.0, clamp(t, 0.0, 1.0));
  vec2 f = texture(iChannel0, vec2(hz / 24000.0, 0.0)).xy;
  return clamp(((f.x + f.y) * 0.5 - 0.22) / 0.45 + t * 0.25, 0.0, 1.0);
}

// How far into the galaxy a cell is: 1 on the band's spine, 0 outside it. The spine runs
// steeply across the frame so it crosses both margins, and wanders a little along its
// length so it is not a ruled line.
float galaxy(vec2 c, vec2 mid) {
  vec2 q = (c - mid) / VROWS;
  vec2 axis = normalize(vec2(0.42, 1.0));
  float along = dot(q, axis);
  float across = dot(q, vec2(-axis.y, axis.x)) + (fbm(vec2(along * 3.0, 5.0)) - 0.5) * 0.10;
  return exp(-across * across / 0.012);
}

// The dust lane: a dark rift just off the spine, ragged along its edge.
float dust(vec2 c, vec2 mid) {
  vec2 q = (c - mid) / VROWS;
  vec2 axis = normalize(vec2(0.42, 1.0));
  float along = dot(q, axis);
  float across = dot(q, vec2(-axis.y, axis.x)) - 0.018 + (fbm(vec2(along * 5.0, 9.0)) - 0.5) * 0.05;
  return smoothstep(0.022, 0.0, abs(across)) * smoothstep(0.35, 0.65, fbm(c * 0.045 + 3.0));
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 uv = fragCoord / iResolution.xy;
  float edge = abs(uv.y - 0.5) * 2.0;

  // The whole sky drifts on a closed path, so the clip's last frame lands on its first.
  // The constant keeps pixel centres off exact cell edges: loopDrift closes to within float
  // error, not exactly (GPU division is approximate), and a centre sitting exactly on an
  // edge at the loop point floors into the neighbouring cell on one side of the seam.
  vec2 p = fragCoord / cell + vec2(0.1234, 0.2345) + loopDrift(vec2(0.9, 0.35), iTime);
  vec2 c = floor(p);
  vec2 mid = vec2(iResolution.x / cell * 0.5, VROWS * 0.5);

  vec3 col = vec3(0.010, 0.012, 0.026);

  // Haze: the galaxy's glow, lumpy, cut by the dust, stepped into four levels and dithered
  // between them pixel by pixel.
  float gal = galaxy(c, mid);
  float d = dust(c, mid);
  float haze = gal * (0.35 + 0.65 * fbm(c * 0.06)) * (1.0 - 0.85 * d);
  float lvl = floor(haze * 4.0 + bayer4(c)) / 4.0;
  col += mix(vec3(0.030, 0.034, 0.060), vec3(0.070, 0.058, 0.070), fbm(c * 0.03 + 7.0)) * lvl;

  float beatIndex = floor(iBeat);
  float ph = fract(iBeat);
  float hit = 1.0 - clamp(ph / 0.5, 0.0, 1.0);

  // Stars: sparse everywhere, several times denser in the galaxy and clumped there, and
  // thinned by the dust. Each is one cell, plus a four-cell cross when it is a bright one
  // and its turn on the beat comes round.
  for (int k = 0; k < 5; k++) {
    vec2 o = k == 0 ? vec2(0.0) : k == 1 ? vec2(1.0, 0.0) : k == 2 ? vec2(-1.0, 0.0) : k == 3 ? vec2(0.0, 1.0) : vec2(0.0, -1.0);
    vec2 sc = c - o;
    float g = galaxy(sc, mid);
    float density = 0.010 + g * 0.07 * smoothstep(0.3, 0.8, fbm(sc * 0.08 + 11.0)) * (1.0 - dust(sc, mid));
    float h = hash(sc);
    if (h > density) continue;
    // Brightness class from the same draw: the rarest are the bright ones.
    float bright = step(h, density * 0.18);
    float group = floor(hash(sc + 17.0) * 4.0);
    float mine = bright * step(abs(mod(beatIndex, 4.0) - group), 0.5) * hit;
    if (k > 0 && mine < 0.01) continue;

    float e = fft(hash(sc + 5.0));
    float level = mix(0.10, 0.24, bright) + floor(e * 3.0) / 3.0 * mix(0.10, 0.30, bright);
    float warm = hash(sc + 9.0);
    vec3 tint = warm > 0.8 ? vec3(0.95, 0.74, 0.55) : warm > 0.6 ? vec3(0.95, 0.92, 0.85) : vec3(0.62, 0.74, 0.98);
    float b = k == 0 ? level + mine * 0.35 : mine * 0.40;
    // Behind the staff the panel hides them anyway; dim them so nothing shows through.
    b *= mix(0.4, 1.0, step(0.5, edge));
    col = max(col, tint * b);
  }

  fragColor = vec4(col, 1.0);
}
"""

// A one-trace oscilloscope along the bottom margin. A beat turns the trace white and a
// downbeat turns it amber.
//
// The waveform arrives phase-locked (lib/phase-lock.civet: each window starts on the bass
// crossing that best matches the last frame), so a held note stands still. Renders also
// undo their gain trim before quantising it (untrimmedAnalyser in renderer.civet), so this
// sees the song at the level the app plays it, with the full 8 bits of resolution.
quietScope := """
// 320 rows: whole device pixels per cell at 960, 1280, 1920 and 2560.
#define VROWS 320.0
// Centre line and full-scale swing, in rows. The bottom margin is ~70 rows deep.
#define MID 34.0
#define AMP 24.0
// The analyser hands over 512 samples (~10.7 ms at 48 kHz), all of them across the frame.
#define N 512.0

float sampleAt(float i) {
  vec4 a = texture(iChannel0, vec2((i + 0.5) / N, 0.0));
  return (a.z + a.w) - 1.0;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 ip = floor(fragCoord / cell);
  float vcols = floor(iResolution.x / cell);

  vec3 col = vec3(0.012, 0.018, 0.028);

  // The whole scope lives below this row; everything above is the plain ground.
  if (ip.y < MID + AMP + 2.0) {
    float s0 = sampleAt(floor(ip.x / vcols * N));
    float s1 = sampleAt(min(floor((ip.x + 1.0) / vcols * N), N - 1.0));
    float y0 = MID + floor(clamp(s0 * 1.2, -1.0, 1.0) * AMP + 0.5);
    float y1 = MID + floor(clamp(s1 * 1.2, -1.0, 1.0) * AMP + 0.5);
    // Filled between this column and the next, so steep edges stay joined.
    float trace = step(min(y0, y1), ip.y) * step(ip.y, max(y0, y1));

    // The centre line, dotted, its dots walking left to right a tenth of a beat per cell so
    // the ground under the wave counts time.
    float dots = step(mod(ip.x - fract(iBeat) * 10.0, 10.0), 0.5) * step(abs(ip.y - MID), 0.5);

    float ph = fract(iBeat);
    float down = step(mod(floor(iBeat), 4.0), 0.5);
    float hit = 1.0 - clamp(ph / 0.4, 0.0, 1.0);
    vec3 lineCol = mix(vec3(0.22, 0.62, 0.66), mix(vec3(0.85, 0.92, 0.95), vec3(0.98, 0.66, 0.28), down), hit * 0.8);

    col = mix(col, vec3(0.10, 0.16, 0.20), dots);
    col = mix(col, lineCol * 0.8, trace);
  }

  fragColor = vec4(col, 1.0);
}
"""

// One hairline spectrum trace along the bottom margin, bass at the left and treble at the
// right, over a faint flat fill: the song's spectrum as a skyline under the staff. A beat
// turns the trace white and a downbeat turns it amber.
//
quietSpectrum := """
// 320 rows: whole device pixels per cell at 960, 1280, 1920 and 2560.
#define VROWS 320.0
// Baseline and full-scale height, in rows. The bottom margin is ~70 rows deep; the trace
// peaks well short of the staff so the notes spilling below it stay clear.
#define BASE 12.0
#define AMP 40.0

// Log-frequency 40 Hz..5 kHz. Each column averages eight taps across its own slice of the
// spectrum, so the treble end (where one column covers many bins) is a line, not a comb.
float level(float t0, float t1) {
  float s = 0.0;
  for (int k = 0; k < 8; k++) {
    float t = mix(t0, t1, (float(k) + 0.5) / 8.0);
    float hz = 40.0 * pow(125.0, clamp(t, 0.0, 1.0));
    vec2 f = texture(iChannel0, vec2(hz / 24000.0, 0.0)).xy;
    s += (f.x + f.y) * 0.5;
  }
  s *= 0.125;
  // Stretched to the range a -24 dB render uses, tilted up toward the quieter treble, then
  // eased into the top so a loud bass rounds off well under the ceiling instead of
  // flattening on it.
  float e = max(0.0, (s - 0.28) / 0.55 + t0 * 0.20);
  return e / (0.6 + e) * 1.4;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 ip = floor(fragCoord / cell);
  float vcols = floor(iResolution.x / cell);

  vec3 col = vec3(0.012, 0.018, 0.028);

  // The trace at this column's edges, filled between them so steep slopes stay joined.
  float ta = ip.x / vcols, tb = (ip.x + 1.0) / vcols, tc = (ip.x + 2.0) / vcols;
  float y0 = BASE + floor(level(ta, tb) * AMP);
  float y1 = BASE + floor(level(tb, tc) * AMP);
  float lo = min(y0, y1), hi = max(y0, y1);
  float trace = step(lo, ip.y) * step(ip.y, hi);
  float under = step(BASE, ip.y) * step(ip.y, y0 - 1.0) * (1.0 - trace);

  // Beat ticks along the baseline, scrolling left to right one cell per tenth of a beat, so
  // the ground under the trace counts time.
  float tick = step(mod(ip.x - fract(iBeat) * 10.0, 10.0), 0.5) * step(abs(ip.y - (BASE - 2.0)), 0.5);

  float ph = fract(iBeat);
  float down = step(mod(floor(iBeat), 4.0), 0.5);
  float hit = 1.0 - clamp(ph / 0.4, 0.0, 1.0);
  vec3 lineCol = mix(vec3(0.22, 0.62, 0.66), mix(vec3(0.85, 0.92, 0.95), vec3(0.98, 0.66, 0.28), down), hit * 0.8);

  col = mix(col, vec3(0.10, 0.16, 0.20), tick);
  col = mix(col, vec3(0.035, 0.070, 0.085), under);
  col = mix(col, lineCol * 0.8, trace);

  fragColor = vec4(col, 1.0);
}
"""

// Flat bands of sunset sky, stepped rather than graded, mirrored top and bottom. The bass
// pushes the band edges out; each beat lifts one band in turn.
duskSteps := """
#define VROWS 160.0
#define BANDS 7.0

float fft(float t) {
  vec2 f = texture(iChannel0, vec2(pow(clamp(t, 0.0, 1.0), 3.0), 0.0)).xy;
  return (f.x + f.y) * 0.5;
}

float bass() {
  float s = 0.0;
  for (int i = 0; i < 8; i++) {
    s += fft(float(i) / 64.0);
  }
  return s * 0.125;
}

float bayer4(vec2 p) {
  vec2 q = mod(p, 4.0);
  float i = q.x + q.y * 4.0;
  // 4x4 ordered-dither thresholds, row by row.
  return i < 1.0 ? 0.0 : i < 2.0 ? 8.0 : i < 3.0 ? 2.0 : i < 4.0 ? 10.0
       : i < 5.0 ? 12.0 : i < 6.0 ? 4.0 : i < 7.0 ? 14.0 : i < 8.0 ? 6.0
       : i < 9.0 ? 3.0 : i < 10.0 ? 11.0 : i < 11.0 ? 1.0 : i < 12.0 ? 9.0
       : i < 13.0 ? 15.0 : i < 14.0 ? 7.0 : i < 15.0 ? 13.0 : 5.0;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  float cell = iResolution.y / VROWS;
  vec2 ip = floor(fragCoord / cell);
  vec2 uv = (ip + 0.5) * cell / iResolution.xy;

  // 0 at the staff's middle, 1 at the frame's edge. The bass and a slow sway both push the
  // steps outward, each a bounded displacement rather than a rate, so the loop closes.
  float edge = abs(uv.y - 0.5) * 2.0;
  float sway = 0.02 * sin(iTime * loopRateAngular(0.5));
  float d = clamp((edge - 0.42) / 0.58 - bass() * 0.10 + sway, 0.0, 1.0);

  // A dithered step between bands: the band index comes from the position plus an
  // ordered-dither offset, so each edge is a hard checker of the two colours around it.
  float b = floor(d * BANDS + (bayer4(ip) / 16.0 - 0.5) * 0.9);
  b = clamp(b, 0.0, BANDS - 1.0);

  // A sunset read from overhead down to the horizon, the horizon at the frame's edge:
  // indigo, violet, magenta, crimson, red-orange, orange, gold. One colour per band, so the
  // steps are the palette itself rather than a blend between two ends.
  vec3 col = b < 0.5 ? vec3(0.022, 0.018, 0.060)
           : b < 1.5 ? vec3(0.085, 0.030, 0.140)
           : b < 2.5 ? vec3(0.200, 0.040, 0.180)
           : b < 3.5 ? vec3(0.340, 0.060, 0.120)
           : b < 4.5 ? vec3(0.500, 0.130, 0.070)
           : b < 5.5 ? vec3(0.640, 0.260, 0.070)
           :           vec3(0.740, 0.440, 0.130);

  // Each beat lifts the next band out from the middle, round the four outer bands.
  float ph = fract(iBeat);
  float hit = 1.0 - clamp(ph / 0.4, 0.0, 1.0);
  float lifted = BANDS - 4.0 + mod(floor(iBeat), 4.0);
  col += vec3(0.20, 0.12, 0.06) * hit * step(abs(b - lifted), 0.5);

  fragColor = vec4(col, 1.0);
}
"""

export default {
  "orig-quiet-meters":
    name: "Quiet Meters"
    code: quietMeters
  "orig-still-stars":
    name: "Still Stars"
    code: stillStars
  "orig-quiet-scope":
    name: "Quiet Scope"
    code: quietScope
  "orig-quiet-spectrum":
    name: "Quiet Spectrum"
    code: quietSpectrum
  "orig-dusk-steps":
    name: "Dusk Steps"
    code: duskSteps
  "orig-skyline-mirror":
    name: "Skyline Mirror"
    code: skylineMirror
  "orig-skyline-depth":
    name: "Skyline Depth"
    code: skylineDepth
  "orig-skyline-radial":
    name: "Skyline Radial"
    code: skylineRadial
  "orig-skyline-scope":
    name: "Skyline Scope"
    code: skylineScope
  "orig-skyline-grid":
    name: "Skyline Grid"
    code: skylineGrid
  "orig-skyline-bloom":
    name: "Skyline Bloom"
    code: skylineBloom
  "orig-chroma-key":
    name: "Chroma Key"
    code: chromaKey
  "orig-neon-storm":
    name: "Neon Storm"
    code: neonStorm
  "orig-neon-daybreak":
    name: "Neon Daybreak"
    code: neonDaybreak
  "orig-neon-ocean":
    name: "Neon Ocean"
    code: neonOcean
  "orig-neon-ridgeline":
    name: "Neon Ridgeline"
    code: neonRidgeline
  "orig-vapor-checker":
    name: "Vapor Checker"
    code: vaporChecker
  "orig-neon-corridor":
    name: "Neon Corridor"
    code: neonCorridor
  "orig-amber-crt":
    name: "Amber CRT"
    code: amberCrt
  "orig-neon-horizon":
    name: "Neon Horizon"
    code: neonHorizon
  "orig-spectrum-skyline":
    name: "Spectrum Skyline"
    code: spectrumSkyline
  "orig-chrome-tunnel":
    name: "Chrome Tunnel"
    code: chromeTunnel
  "orig-beat-bloom":
    name: "Beat Bloom"
    code: beatBloom
  "orig-spectrum-rain":
    name: "Spectrum Rain"
    code: spectrumRain
  "orig-waveform-ribbon":
    name: "Waveform Ribbon"
    code: waveformRibbon
}



